const SELECTORS = {
    progress: ".bux-multi-form__progress-filled",
    nextButton: ".bux-multi-form__next-button",
    backButton: ".bux-multi-form__back-button",
    stepIcons: ".bux-multi-form__step-icon",
    stepLabels: ".bux-multi-form__step-label",
    stepContainer: ".bux-multi-form__step-container",
    forms: ".bux-multi-form__form",
    iconCurrent: "bux-multi-form__step-icon--current",
    iconComplete: "bux-multi-form__step-icon--complete",
    horizontal: ".bux-multi-form__layout--horizontal",
    vertical: ".bux-multi-form__layout--vertical",
    container: ".bux-multi-form",
};

class MultiStepForm {
    private progress: HTMLElement;
    private nextButton: HTMLButtonElement;
    private backButton: HTMLButtonElement;
    private stepIcons: NodeListOf<HTMLElement>;
    private stepLabels: NodeListOf<HTMLElement>;
    private readonly forms: NodeListOf<HTMLElement>;
    private readonly isVertical: boolean;
    private currentStep: number;
    private stepContainer: HTMLElement | null | undefined = undefined;
    constructor(private readonly container: HTMLElement) {
        const progress = this.container.querySelector<HTMLElement>(SELECTORS.progress);
        const nextButton = this.container.querySelector(SELECTORS.nextButton)?.querySelector("button");
        const backButton = this.container.querySelector(SELECTORS.backButton)?.querySelector("button");

        if (!progress || !nextButton || !backButton) {
            throw new TypeError("Buckeye UX Multi-Step Form is missing required elements.");
        }

        this.progress = progress;
        this.nextButton = nextButton;
        this.backButton = backButton;
        this.stepIcons = this.container.querySelectorAll<HTMLElement>(SELECTORS.stepIcons);
        this.stepLabels = this.container.querySelectorAll<HTMLElement>(SELECTORS.stepLabels);
        this.forms = this.container.querySelectorAll<HTMLElement>(SELECTORS.forms);

        this.isVertical = this.container.querySelector<HTMLElement>(SELECTORS.vertical) !== null;
        this.currentStep = 0;

        this.setInitialFormDisplay();
        this.bindButtonEvents();
    }


    setInitialFormDisplay() {
        this.setFormsContainerMinHeight();
        this.backButton.style.display = "none";
    }

    bindButtonEvents() {
        this.nextButton.addEventListener("click", (event) => this.handleButtonClick(1, event));
        this.backButton.addEventListener("click", (event) => this.handleButtonClick(-1, event));
    }

    handleButtonClick(direction: 1 | -1, event: Event) {
        event.preventDefault();
        this.controlForm(direction);
    }

    updateProgress() {
        const progressAmount = (100 / this.stepIcons.length) * this.currentStep;
        if (this.isVertical) {
            this.progress.style.height = `${progressAmount}%`;
        } else {
            this.progress.style.width = `${progressAmount}%`;
        }
    }

    updateStepIcons(direction: 1 | -1) {
        this.stepIcons[this.currentStep].classList.remove(SELECTORS.iconCurrent);
        if (direction === 1) {
            this.stepIcons[this.currentStep].classList.add(SELECTORS.iconComplete);
        } else if (direction === -1) {
            this.stepIcons[this.currentStep].classList.remove(SELECTORS.iconComplete);
        }
        this.stepIcons[this.currentStep + direction].classList.add(SELECTORS.iconCurrent);
    }

    updateStepForm(direction: 1 | -1) {
        this.forms[this.currentStep].style.display = "none";
        this.forms[this.currentStep + direction].style.display = "block";
    }

    updateButtons() {
        if (this.currentStep === 0) {
            this.backButton.style.display = "none";
        } else {
            this.backButton.style.display = "block";
            const backButtonLabel = this.getCurrentStepLabel(this.currentStep - 1);
            this.backButton.textContent = `Back: ${backButtonLabel}`;
        }

        if (this.currentStep === this.stepIcons.length - 1) {
            this.nextButton.textContent = "Submit";
        } else {
            const nextButtonLabel = this.getCurrentStepLabel(this.currentStep + 1);
            this.nextButton.textContent = `Next: ${nextButtonLabel}`;
        }
    }

    controlForm(direction: 1 | -1) {
        if (
            (direction === 1 && this.currentStep < this.stepIcons.length - 1) ||
            (direction === -1 && this.currentStep > 0)
        ) {
            this.updateStepIcons(direction);
            this.updateStepForm(direction);
            this.currentStep += direction;
            this.updateProgress();
            this.updateButtons();
        }
    }

    getCurrentStepLabel(index: number) {
        return this.stepLabels[index].textContent;
    }

    setFormsContainerMinHeight() {
        this.container.style.visibility = "hidden";

        let maxHeight = 0;
        for (const form of this.forms) {
            form.style.display = "block";
            maxHeight = Math.max(maxHeight, this.container.offsetHeight);
            form.style.display = "none";
        }

        this.container.style.minHeight = `${maxHeight}px`;
        this.forms[0].style.display = "block";
        this.container.style.visibility = "visible";

        if (this.isVertical) {
            this.stepContainer = this.container.querySelector<HTMLElement>(SELECTORS.stepContainer);
            if (this.stepContainer) {
                this.stepContainer.style.minHeight = `${maxHeight}px`;
            }
        }
    }
}

export const init = () => {
    const multiFormDivs = document.querySelectorAll(SELECTORS.container);

    for (const formDiv of multiFormDivs) {
        const formId = formDiv.id;
        if (formId) {
            const container = document.querySelector<HTMLElement>(`#${formId}`);
            if (!container) {
                continue;
            }
            new MultiStepForm(container);
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener("DOMContentLoaded", init);
} else {
    init();
}